// Generates a 1080x1920 "Instagram Story" PNG for an achievement celebration,
// entirely client-side - same no-server-round-trip philosophy as the
// printable worksheets feature (src/utils/worksheet/pdf.ts hand-writes PDF
// bytes in the browser; this hand-draws a PNG via the Canvas API instead).
//
// Instagram has no API for posting directly to a Story from a third-party
// site. This module only produces the image; AchievementCelebrationDialog.tsx
// offers it to the learner via the Web Share API (navigator.share with
// files, when the browser/OS genuinely supports it - see
// isWebShareFilesSupported, which feature-detects at runtime rather than
// assuming support from a browser allowlist) or a plain download as the
// universal fallback.

export const STORY_WIDTH = 1080;
export const STORY_HEIGHT = 1920;

// Instagram's own Story UI (profile avatar/username at top, reply box and
// sticker tray at bottom) covers roughly the top/bottom ~250px of a
// 1920-tall story in practice - keep essential content (badge, title, XP,
// branding) inside this band.
const SAFE_TOP = 250;

// Mirrors the light-mode hex values from src/styles/_variables.scss. SCSS
// custom properties aren't readable from a canvas draw, and this image is a
// standalone asset meant to be shared/downloaded outside the app, so (like
// this codebase's other deliberately theme-invariant brand constants, e.g.
// the WaniKani SRS badge colors in _variables.scss) it always uses the
// light-mode brand palette regardless of the viewer's current in-app theme.
const COLOR_DARK = "#1a1a2e"; // $colorDark
const COLOR_DARK_MEDIUM = "#16213e"; // $colorDarkMedium
const COLOR_DARK_LIGHT = "#0f3460"; // $colorDarkLight
const COLOR_PRIMARY = "#e94560"; // $colorPrimary
const COLOR_SURFACE = "#ffffff"; // $colorSurface
const COLOR_HEADING = "#1a1a2e"; // $colorHeading
const COLOR_XP_TEXT = "#3e8b50"; // $colorXpText
const COLOR_WHITE = "#ffffff";

// Same Japanese-capable font stack used elsewhere in this app (e.g.
// .vocabWord, .conjugationKanji) - achievement/lesson titles can contain
// Japanese text (e.g. grammar celebrations like "Particle は").
const FONT_STACK = '"Noto Sans JP", "Hiragino Sans", "Yu Gothic", sans-serif';

export type CelebrationStoryCopy = {
    eyebrow: string;
    title: string;
    xpLabel?: string;
    invitation: string;
    brandName: string;
    brandUrl: string;
};

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error(`Couldn't load ${src}`));
        image.src = src;
    });
}

function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number): void {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + width, y, x + width, y + height, radius);
    ctx.arcTo(x + width, y + height, x, y + height, radius);
    ctx.arcTo(x, y + height, x, y, radius);
    ctx.arcTo(x, y, x + width, y, radius);
    ctx.closePath();
}

export function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
    const words = text.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let current = "";
    for (const word of words) {
        const attempt = current ? `${current} ${word}` : word;
        if (current && ctx.measureText(attempt).width > maxWidth) {
            lines.push(current);
            current = word;
        } else {
            current = attempt;
        }
    }
    if (current) lines.push(current);
    return lines;
}

function truncateWithEllipsis(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
    if (ctx.measureText(text).width <= maxWidth) return text;
    let truncated = text;
    while (truncated.length > 1 && ctx.measureText(`${truncated}…`).width > maxWidth) {
        truncated = truncated.slice(0, -1);
    }
    return `${truncated}…`;
}

export type FittedTitle = { fontSize: number; lineHeight: number; lines: string[] };

// Picks the largest font size (stepping down from maxFont to minFont) that
// wraps `text` into at most `maxLines` lines fitting inside
// maxWidth x maxHeight - so a long real lesson title (this app's data
// includes ones like "Describing nouns: people who do, like, have, or wear")
// always ends up wrapped/scaled rather than overflowing or getting clipped.
// Only in a pathological case where even the smallest size and the line
// budget still can't fit does the last line get an ellipsis - a deliberate,
// visible truncation marker, never a silent clip.
export function fitTitle(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxHeight: number, maxFont: number, minFont: number, maxLines: number): FittedTitle {
    const lineHeightRatio = 1.22;
    for (let fontSize = maxFont; fontSize >= minFont; fontSize -= 2) {
        ctx.font = `800 ${fontSize}px ${FONT_STACK}`;
        const lines = wrapLines(ctx, text, maxWidth);
        const lineHeight = fontSize * lineHeightRatio;
        const fitsWidth = lines.every((line) => ctx.measureText(line).width <= maxWidth);
        if (fitsWidth && lines.length <= maxLines && lines.length * lineHeight <= maxHeight) {
            return { fontSize, lineHeight, lines };
        }
    }
    // Even the smallest size couldn't fit the line/height budget (or, rarely,
    // wrapLines left an unbreakable single word wider than maxWidth on its
    // own line) - fall back to the floor size and ellipsis-truncate any line
    // that is still too wide, so the image never actually clips text.
    ctx.font = `800 ${minFont}px ${FONT_STACK}`;
    const lines = wrapLines(ctx, text, maxWidth).slice(0, maxLines).map((line) => truncateWithEllipsis(ctx, line, maxWidth));
    return { fontSize: minFont, lineHeight: minFont * lineHeightRatio, lines };
}

function drawBackground(ctx: CanvasRenderingContext2D): void {
    const gradient = ctx.createLinearGradient(0, 0, 0, STORY_HEIGHT);
    gradient.addColorStop(0, COLOR_DARK);
    gradient.addColorStop(0.55, COLOR_DARK_MEDIUM);
    gradient.addColorStop(1, COLOR_DARK_LIGHT);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, STORY_WIDTH, STORY_HEIGHT);
}

// Draws the turtle badge (logo512.png already has its own circular coral
// background baked in) and returns its bottom y so the card below can be
// positioned relative to it.
function drawBadge(ctx: CanvasRenderingContext2D, logo: HTMLImageElement): number {
    const size = 260;
    const x = (STORY_WIDTH - size) / 2;
    const y = SAFE_TOP + 40;
    ctx.save();
    ctx.shadowColor = "rgba(0, 0, 0, 0.35)";
    ctx.shadowBlur = 40;
    ctx.shadowOffsetY = 16;
    ctx.drawImage(logo, x, y, size, size);
    ctx.restore();
    return y + size;
}

// Draws the white card (eyebrow + title + optional XP line) and returns its
// bottom y so the footer below can be positioned relative to it.
function drawCard(ctx: CanvasRenderingContext2D, copy: CelebrationStoryCopy, cardTop: number): number {
    const cardX = 80;
    const cardWidth = STORY_WIDTH - cardX * 2;
    const paddingX = 84;
    const paddingTop = 64;
    const paddingBottom = 64;
    const maxTitleWidth = cardWidth - paddingX * 2;
    const centerX = STORY_WIDTH / 2;

    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";

    const eyebrowFontSize = 30;
    const eyebrowGap = 30;
    const titleGapTop = 22;
    const fitted = fitTitle(ctx, copy.title, maxTitleWidth, 600, 72, 36, 5);
    const xpGap = 36;
    const xpFontSize = 46;

    let cardHeight = paddingTop + eyebrowFontSize + eyebrowGap + titleGapTop + fitted.lines.length * fitted.lineHeight + paddingBottom;
    if (copy.xpLabel) cardHeight += xpGap + xpFontSize;

    roundRectPath(ctx, cardX, cardTop, cardWidth, cardHeight, 48);
    ctx.save();
    ctx.fillStyle = COLOR_SURFACE;
    ctx.shadowColor = "rgba(15, 33, 61, 0.35)";
    ctx.shadowBlur = 48;
    ctx.shadowOffsetY = 20;
    ctx.fill();
    ctx.restore();

    let cursorY = cardTop + paddingTop + eyebrowFontSize * 0.8;
    ctx.font = `800 ${eyebrowFontSize}px ${FONT_STACK}`;
    ctx.fillStyle = COLOR_PRIMARY;
    ctx.fillText(copy.eyebrow, centerX, cursorY);

    cursorY += eyebrowGap + titleGapTop + fitted.fontSize * 0.86;
    ctx.font = `800 ${fitted.fontSize}px ${FONT_STACK}`;
    ctx.fillStyle = COLOR_HEADING;
    fitted.lines.forEach((line, index) => ctx.fillText(line, centerX, cursorY + index * fitted.lineHeight));
    cursorY += (fitted.lines.length - 1) * fitted.lineHeight;

    if (copy.xpLabel) {
        cursorY += xpGap + xpFontSize * 0.8;
        ctx.font = `800 ${xpFontSize}px ${FONT_STACK}`;
        ctx.fillStyle = COLOR_XP_TEXT;
        ctx.fillText(copy.xpLabel, centerX, cursorY);
    }

    return cardTop + cardHeight;
}

// Invitation line + brand row (turtle mark, "NihongoTech" wordmark and the
// literal nihongotech.teqseq.com text) baked into the image pixels, so the
// branding survives the image being downloaded/shared standalone.
function drawFooter(ctx: CanvasRenderingContext2D, copy: CelebrationStoryCopy, logo: HTMLImageElement, cardBottom: number): void {
    const centerX = STORY_WIDTH / 2;

    let y = cardBottom + 96;
    ctx.textAlign = "center";
    ctx.font = `600 38px ${FONT_STACK}`;
    ctx.fillStyle = "rgba(255, 255, 255, 0.92)";
    ctx.fillText(copy.invitation, centerX, y);

    y += 76;
    const iconSize = 64;
    ctx.font = `800 42px ${FONT_STACK}`;
    const brandTextWidth = ctx.measureText(copy.brandName).width;
    const rowGap = 18;
    const rowWidth = iconSize + rowGap + brandTextWidth;
    const rowStartX = centerX - rowWidth / 2;
    ctx.drawImage(logo, rowStartX, y - iconSize * 0.76, iconSize, iconSize);
    ctx.textAlign = "left";
    ctx.fillStyle = COLOR_WHITE;
    ctx.fillText(copy.brandName, rowStartX + iconSize + rowGap, y);

    y += 58;
    ctx.textAlign = "center";
    ctx.font = `500 32px ${FONT_STACK}`;
    ctx.fillStyle = "rgba(255, 255, 255, 0.78)";
    ctx.fillText(copy.brandUrl, centerX, y);
}

export function isWebShareFilesSupported(file: File): boolean {
    if (typeof navigator === "undefined" || typeof navigator.canShare !== "function") return false;
    try {
        return navigator.canShare({ files: [file] });
    } catch {
        return false;
    }
}

export async function generateCelebrationStoryImage(copy: CelebrationStoryCopy, logoSrc = "/logo512.png"): Promise<Blob> {
    const logo = await loadImage(logoSrc);
    const canvas = document.createElement("canvas");
    canvas.width = STORY_WIDTH;
    canvas.height = STORY_HEIGHT;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D context is unavailable");

    drawBackground(ctx);
    const badgeBottom = drawBadge(ctx, logo);
    const cardBottom = drawCard(ctx, copy, badgeBottom + 50);
    drawFooter(ctx, copy, logo, cardBottom);

    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error("Couldn't create the share image"));
        }, "image/png");
    });
}
